import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { employeesAPI } from '../../../services/api';
import {
  Employee,
  EmployeeStats,
  STATUT_EMPLOYE_OPTIONS,
  TYPE_CONTRAT_OPTIONS,
} from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getFullFileUrl } from '../../../utils/fileUrl';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import EmployeeFormDialog from './EmployeeFormDialog';
import PermissionButton from '../../../components/PermissionButton';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
import CustomTablePagination from '../../../components/CustomTablePagination';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const HR_COLOR = '#00897B';

const EMPLOYEE_FILTER_SCHEMA = {
  matricule:     { type: 'text',      param: 'matricule' },
  nom_complet:   { type: 'text',      param: 'nom_complet' },
  cin:           { type: 'text',      param: 'cin' },
  fonction:      { type: 'text',      param: 'fonction' },
  type_contrat:  { type: 'select',    param: 'type_contrat' },
  date_embauche: { type: 'dateRange', paramFrom: 'date_embauche_from', paramTo: 'date_embauche_to' },
  statut:        { type: 'select',    param: 'statut' },
} as const;

const EmployeeList: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);

  const { showSuccess, showError } = useAlert();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    EMPLOYEE_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await employeesAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        ...toQueryParams(),
      });
      setEmployees(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, filters, showError]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await employeesAPI.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setViewMode(false);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setViewMode(false);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleViewEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setViewMode(true);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;

    try {
      await employeesAPI.delete(employeeToDelete.id);
      showSuccess('Employé supprimé avec succès');
      fetchEmployees();
      fetchStats();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
    }
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setSelectedEmployee(null);
    setViewMode(false);
  };

  const handleFormSuccess = () => {
    fetchEmployees();
    fetchStats();
    handleFormClose();
  };

  const getStatusChip = (statut: string) => {
    const statusConfig: Record<string, { color: 'success' | 'error' | 'warning'; label: string; icon: React.ReactElement }> = {
      ACTIF: { color: 'success', label: 'Actif', icon: <ActiveIcon sx={{ fontSize: 16 }} /> },
      INACTIF: { color: 'error', label: 'Inactif', icon: <InactiveIcon sx={{ fontSize: 16 }} /> },
      SUSPENDU: { color: 'warning', label: 'Suspendu', icon: <InactiveIcon sx={{ fontSize: 16 }} /> },
    };

    const config = statusConfig[statut] || { color: 'default' as const, label: statut, icon: null };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const getContractChip = (type: string) => {
    const contractConfig: Record<string, { color: string; label: string }> = {
      CDI: { color: '#4CAF50', label: 'CDI' },
      CDD: { color: '#2196F3', label: 'CDD' },
      ANAPEC: { color: '#FF9800', label: 'ANAPEC' },
      INTERIM: { color: '#9C27B0', label: 'Intérim' },
      ETRANGER: { color: '#607D8B', label: 'Étranger' },
      STAGE: { color: '#00BCD4', label: 'Stage' },
    };

    const config = contractConfig[type] || { color: '#757575', label: type };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: 'white',
          fontWeight: 500,
        }}
      />
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: HR_COLOR, fontWeight: 600 }}>
         
        </Typography>
        <PermissionButton
          moduleName="HR"
          operation="add"
          miniModuleName="Employés"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddEmployee}
          sx={{
            backgroundColor: HR_COLOR,
            '&:hover': { backgroundColor: '#00695C' },
          }}
        >
          Ajouter un Employé
        </PermissionButton>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: `4px solid ${HR_COLOR}` }}>
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Total Employés
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: HR_COLOR }}>
                      {stats.total_employees}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: HR_COLOR, opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #4CAF50' }}>
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Actifs
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                      {stats.active_employees}
                    </Typography>
                  </Box>
                  <ActiveIcon sx={{ fontSize: 40, color: '#4CAF50', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #F44336' }}>
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Inactifs
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#F44336' }}>
                      {stats.inactive_employees}
                    </Typography>
                  </Box>
                  <InactiveIcon sx={{ fontSize: 40, color: '#F44336', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: '4px solid #2196F3' }}>
              <CardContent sx={{ py: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      CDI
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#2196F3' }}>
                      {stats.by_contract_type['CDI'] || 0}
                    </Typography>
                  </Box>
                  <Chip label="CDI" sx={{ backgroundColor: '#2196F3', color: 'white' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Rechercher par nom, matricule, CIN, téléphone..."
            value={search}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: HR_COLOR }} />
                </InputAdornment>
              ),
            }}
          />

          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={fetchEmployees} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={getModuleTableTheme('hr')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Photo</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Matricule
                  <ColumnFilter type="text" value={filters.matricule} onChange={(v) => update('matricule', v)} label="matricule" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom Complet
                  <ColumnFilter type="text" value={filters.nom_complet} onChange={(v) => update('nom_complet', v)} label="nom complet" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  CIN
                  <ColumnFilter type="text" value={filters.cin} onChange={(v) => update('cin', v)} label="CIN" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Fonction
                  <ColumnFilter type="text" value={filters.fonction} onChange={(v) => update('fonction', v)} label="fonction" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Type Contrat
                  <ColumnFilter
                    type="select"
                    value={filters.type_contrat}
                    onChange={(v) => update('type_contrat', v)}
                    label="type contrat"
                    color={HR_COLOR}
                    options={TYPE_CONTRAT_OPTIONS.map(o => ({ value: o.value, label: o.value }))}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date Embauche
                  <ColumnFilter type="dateRange" value={filters.date_embauche} onChange={(v) => update('date_embauche', v)} label="date embauche" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>Ancienneté</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Statut
                  <ColumnFilter
                    type="select"
                    value={filters.statut}
                    onChange={(v) => update('statut', v)}
                    label="statut"
                    color={HR_COLOR}
                    options={STATUT_EMPLOYE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                  />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  Chargement...
                </TableCell>
              </TableRow>
            ) : employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  Aucun employé trouvé
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => (
                <TableRow key={employee.id} hover>
                  <TableCell>
                    <Avatar
                      src={getFullFileUrl(employee.photo_url)}
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: HR_COLOR,
                      }}
                    >
                      {employee.nom_complet?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: HR_COLOR }}>
                      {employee.type_contrat === 'STAGE' ? (
                        <span style={{ color: '#9e9e9e' }}>-</span>
                      ) : (
                        <TruncatedTextWithTooltip text={employee.matricule} maxWidth={80} />
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      <TruncatedTextWithTooltip text={employee.nom_complet} maxWidth={150} />
                    </Typography>
                    {employee.telephone && (
                      <Typography variant="caption" color="textSecondary">
                        <TruncatedTextWithTooltip text={employee.telephone} maxWidth={120} />
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TruncatedTextWithTooltip text={employee.cin} maxWidth={100} />
                  </TableCell>
                  <TableCell>
                    <TruncatedTextWithTooltip text={employee.fonction} maxWidth={150} />
                  </TableCell>
                  <TableCell>{getContractChip(employee.type_contrat)}</TableCell>
                  <TableCell>
                    <TruncatedTextWithTooltip
                      text={employee.date_embauche
                        ? new Date(employee.date_embauche).toLocaleDateString('fr-FR')
                        : '-'}
                      maxWidth={100}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: HR_COLOR, fontWeight: 500 }}>
                      <TruncatedTextWithTooltip text={employee.anciennete} maxWidth={120} />
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(employee.statut)}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <PermissionButton
                        moduleName="HR"
                        operation="view"
                        miniModuleName="Employés"
                        component="IconButton"
                        size="small"
                        onClick={() => handleViewEmployee(employee)}
                        sx={{ color: '#2196F3' }}
                        tooltip="Voir"
                      >
                        <ViewIcon fontSize="small" />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="HR"
                        operation="edit"
                        miniModuleName="Employés"
                        component="IconButton"
                        size="small"
                        onClick={() => handleEditEmployee(employee)}
                        sx={{ color: HR_COLOR }}
                        tooltip="Modifier"
                      >
                        <EditIcon fontSize="small" />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="HR"
                        operation="delete"
                        miniModuleName="Employés"
                        component="IconButton"
                        size="small"
                        onClick={() => handleDeleteClick(employee)}
                        sx={{ color: '#F44336' }}
                        tooltip="Supprimer"
                      >
                        <DeleteIcon fontSize="small" />
                      </PermissionButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <CustomTablePagination
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* Form Dialog */}
      <EmployeeFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        employee={selectedEmployee}
        viewMode={viewMode}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'employé "${employeeToDelete?.nom_complet}"${employeeToDelete?.type_contrat !== 'STAGE' ? ` (${employeeToDelete?.matricule})` : ''} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel
      />
    </Box>
  );
};

export default EmployeeList;
